import { UpdateTransactionDto } from "@common/dto/transactions/update-transaction.dto";
import {
    CategoryCellVariant,
    CategoryCellVariantSchema,
    TransactionColumn,
} from "@common/types/views/transactionViews";
import { TransactionDirectionType } from "@common/categories";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import {
    memo,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useOverlays } from "contexts/OverlayContext";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { Category } from "@common/types/category";
import { Entity } from "@common/types/entity";
import { useCloseAndBlur } from "@hooks/useCloseAndBlur";
import {
    useIsReadonlyOrLocked,
    useIsTableReadonly,
} from "@hooks/useIsReadonlyOrLocked";
import { useCategoryLabelGetter } from "@hooks/useCategoryLabelGetter";
import { getTransactionsTableMeta } from "@hooks/view/useViewTableMeta";
import { useSingleBusinessEntity } from "@hooks/useSingleBusinessEntity";
import { useUncategorizedCategory } from "@hooks/useUncategorizedCategory";
import { Picker } from "@design-system/Picker/Picker.lib";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { StandardModal } from "@design-system/Modal/Modal";
import { SelectBusinessEntityModal } from "@components/entity/SelectBusinessEntityModal";
import { ClockIcon, DottedCircleIcon } from "@icons";
import classNames from "classnames";
import { addTransactionFieldReviews } from "@api/transaction.api";
import { TransactionReviewField } from "@common/types/transactionFieldReview";
import { isCategoryTaxonomyReviewEligible } from "@common/helpers/transactionFieldReviewEligibility";
import { useUser } from "@hooks/useUser";
import { HStack } from "@design-system/HStack/HStack";
import { CategoryCombobox } from "../TransactionSplitV2/CategoryCombobox";
import { CategoryIcon } from "../CategoryIcon";
import { PaginatedTransactionsActionsContext } from "../PaginatedTransactionsTable.context";
import { TransferMatchLink } from "../TransferMatchLink/TransferMatchLink";
import { getCategoryMatchLink } from "./categoryMatchIcon";
import {
    TransactionPreviewData,
    toTransactionPreviewData,
} from "../TransactionPreview/TransactionPreview";
import { updateTransactionInCache } from "../useTransactionsQuery";
import { transactionCellAreEqual } from "./transactionCellComparators";
import {
    isTransactionFieldReviewedByUser,
    mergeTransactionFieldReviews,
} from "./transactionFieldReviewUtils";
import { TransactionFieldReviewButton } from "./TransactionFieldReviewButton";
import styles from "./CategoryCell.module.scss";

export const CategoryCell = memo(
    ({
        row,
        table,
        column,
    }: CellContext<ViewTableFeatures, TransactionListItem, string>) => {
        const user = useUser();
        const { categoriesById, registerCellActionOpener } =
            getTransactionsTableMeta(table);
        const options = column.columnDef.meta as
            | {
                  variant?: CategoryCellVariant;
                  customVariants?: Record<string, string>;
              }
            | undefined;
        const rawVariant =
            options?.customVariants?.variant ?? options?.variant ?? "full";
        const variantParse = CategoryCellVariantSchema.safeParse(rawVariant);
        const variant: CategoryCellVariant = variantParse.success
            ? variantParse.data
            : "full";
        const getCategoryLabel = useCategoryLabelGetter();
        const { upsertTransaction, saveTransaction } = useContext(
            PaginatedTransactionsActionsContext,
        );
        const [isOpen, setIsOpen] = useState(false);
        const { openOverlay } = useOverlays();
        const singleBusinessEntity = useSingleBusinessEntity();
        const uncategorized = useUncategorizedCategory();
        const isReadonlyOrLocked = useIsReadonlyOrLocked(row.original, table);
        const isTableReadonly = useIsTableReadonly(table);
        const isReviewVariant = variant === "review";
        const [switchingToBusinessCategory, setSwitchingToBusinessCategory] =
            useState<Category>();
        const closeCombobox = useCallback(() => {
            setIsOpen(false);
        }, []);
        const closeAndBlur = useCloseAndBlur(closeCombobox);
        const handleCreationModalOpen = useCallback(() => {
            setIsOpen(false);
        }, []);

        const isReviewed = useMemo(
            () =>
                isTransactionFieldReviewedByUser(
                    row.original.fieldReviews,
                    TransactionReviewField.CATEGORY,
                    user?.id,
                ),
            [row.original.fieldReviews, user?.id],
        );

        const matchPreview: TransactionPreviewData | null = useMemo(
            () => toTransactionPreviewData(row.original.pairedTransfer),
            [row.original.pairedTransfer],
        );

        // Confirmed matches surface the link icon: a paired transfer, a cash
        // invoice/bill match, or an accrual match. A document suggestion on
        // its own does not. A transfer pair still does, even when a transfer
        // suggestion points at the same partner.
        const matchLink = useMemo(
            () => getCategoryMatchLink(row.original),
            [row.original],
        );

        const handleUpdate = useCallback(
            async (payload: UpdateTransactionDto, nextCategory?: Category) => {
                // Resolve the category the transaction is being moved to so
                // both the optimistic update and the review-eligibility gate
                // can key off its taxonomy. Prefer the caller-supplied category
                // (from the combobox) and fall back to the hoisted lookup.
                const updatedCategory =
                    nextCategory ??
                    (payload.categoryId
                        ? categoriesById?.[payload.categoryId]
                        : undefined);
                // Optimistic UI update: enrich with category name so the cell
                // reflects the change immediately. Gracefully skipped when the
                // category can't be resolved (e.g. outside TransactionsView);
                // the server response will still update the UI via query refetch.
                if (payload.categoryId && updatedCategory) {
                    upsertTransaction({
                        ...row.original,
                        categoryId: payload.categoryId,
                        category: updatedCategory,
                    });
                }
                await saveTransaction(row.original, payload);
                // In review variant, automatically mark the category field as
                // reviewed after a successful change — but never for an
                // uncategorized category, which is never eligible for review
                // (shares the backend's taxonomy eligibility check).
                if (
                    isReviewVariant &&
                    payload.categoryId &&
                    isCategoryTaxonomyReviewEligible(updatedCategory?.taxonomy)
                ) {
                    const reviews = await addTransactionFieldReviews(
                        row.original.id,
                        [TransactionReviewField.CATEGORY],
                    );
                    updateTransactionInCache(row.original.id, (t) => ({
                        ...t,
                        fieldReviews: mergeTransactionFieldReviews(
                            t.fieldReviews,
                            reviews,
                        ),
                    }));
                }
            },
            [
                isReviewVariant,
                saveTransaction,
                row.original,
                upsertTransaction,
                categoriesById,
            ],
        );

        const selectedCategory = useMemo(() => {
            if (row.original.categoryId === uncategorized?.id) {
                return undefined;
            }
            return row.original.category;
        }, [row.original.category, row.original.categoryId, uncategorized?.id]);

        const handleCategoryChange = useCallback(
            async (selected: Category | undefined) => {
                if (!selected) {
                    return;
                }

                if (!selected.isBusiness) {
                    await handleUpdate({ categoryId: selected.id }, selected);
                    closeAndBlur();
                    return;
                }

                if (row.original.isBusiness) {
                    await handleUpdate({ categoryId: selected.id }, selected);
                    closeAndBlur();
                    return;
                }

                if (singleBusinessEntity) {
                    await handleUpdate(
                        {
                            categoryId: selected.id,
                            entityId: singleBusinessEntity.id,
                        },
                        selected,
                    );
                    closeAndBlur();
                    return;
                }

                setSwitchingToBusinessCategory(selected);
            },
            [
                handleUpdate,
                row.original.isBusiness,
                singleBusinessEntity,
                closeAndBlur,
            ],
        );

        const onBusinessSelected = useCallback(
            async (entity: Entity) => {
                if (!switchingToBusinessCategory) {
                    return;
                }
                await handleUpdate(
                    {
                        categoryId: switchingToBusinessCategory.id,
                        entityId: entity.id,
                    },
                    switchingToBusinessCategory,
                );
                setSwitchingToBusinessCategory(undefined);
                closeAndBlur();
            },
            [handleUpdate, switchingToBusinessCategory, closeAndBlur],
        );

        useEffect(() => {
            if (!registerCellActionOpener) {
                return;
            }

            return registerCellActionOpener({
                columnId: TransactionColumn.CATEGORY,
                rowId: row.original.id,
                open: () => {
                    if (!isReadonlyOrLocked) {
                        setIsOpen(true);
                    }
                },
            });
        }, [registerCellActionOpener, row.original.id, isReadonlyOrLocked]);

        const transactionDirection =
            row.original.amount > 0
                ? TransactionDirectionType.incoming
                : TransactionDirectionType.outgoing;
        const categoryName =
            selectedCategory && row.original.categoryId
                ? getCategoryLabel(
                      row.original.categoryId,
                      transactionDirection,
                  )
                : "";
        const iconCategoryId = selectedCategory?.id ?? uncategorized?.id;
        const isUncategorized = row.original.categoryId === uncategorized?.id;
        const transactionCategoryWasNotAssigned =
            "inReview" in row.original &&
            row.original.inReview &&
            !row.original.categoryIdLastUpdatedAt;

        const triggerLabel = useMemo(() => {
            if (!isUncategorized) {
                return categoryName || "Select category";
            }

            return transactionCategoryWasNotAssigned
                ? "Categorizing"
                : "Select category";
        }, [categoryName, isUncategorized, transactionCategoryWasNotAssigned]);

        const triggerContent = useMemo(() => {
            if (isUncategorized) {
                return (
                    <HStack
                        as="span"
                        gap={8}
                        align="center"
                        className={styles.uncategorizedTrigger}
                    >
                        {transactionCategoryWasNotAssigned ? (
                            <ClockIcon className="icon-color-gray-500" />
                        ) : (
                            <DottedCircleIcon className="icon-color-gray-500" />
                        )}
                        <BreakableText
                            text={triggerLabel}
                            className={styles.categoryLabel}
                            showTooltip
                        />
                    </HStack>
                );
            }

            return (
                <span className={styles.categoryTrigger}>
                    {iconCategoryId ? (
                        <CategoryIcon categoryId={iconCategoryId} />
                    ) : null}
                    <BreakableText
                        text={triggerLabel}
                        className={styles.categoryLabel}
                        showTooltip
                    />
                </span>
            );
        }, [
            iconCategoryId,
            isUncategorized,
            transactionCategoryWasNotAssigned,
            triggerLabel,
        ]);

        const trigger = useMemo(
            () => (
                <Picker.CellContent
                    readOnly={isReadonlyOrLocked}
                    className={classNames(styles.categoryCell, {
                        [styles.reviewVariant]: isReviewVariant,
                        [styles.isReviewed]: isReviewVariant && isReviewed,
                    })}
                    value={triggerContent}
                    aria-label={triggerLabel}
                />
            ),
            [
                isReadonlyOrLocked,
                triggerContent,
                triggerLabel,
                isReviewVariant,
                isReviewed,
            ],
        );

        return (
            <ViewTableCell captureClicks className={styles.categoryCell}>
                <CategoryCombobox
                    selectedCategory={selectedCategory}
                    onChange={handleCategoryChange}
                    suggestedCategories={row.original.suggestedCategories ?? []}
                    disabled={isReadonlyOrLocked}
                    hideArrow
                    open={isOpen}
                    onOpenChange={setIsOpen}
                    onCreationModalOpen={handleCreationModalOpen}
                    defaultParentCategoryId={
                        selectedCategory?.parentCategoryId ??
                        selectedCategory?.id
                    }
                    trigger={trigger}
                />

                {isReviewVariant && (
                    <TransactionFieldReviewButton
                        transaction={row.original}
                        isReadonly={isTableReadonly}
                        field={TransactionReviewField.CATEGORY}
                        reviewTooltip="Review category"
                        disableMarking={
                            !isCategoryTaxonomyReviewEligible(
                                row.original.category?.taxonomy,
                            )
                        }
                    />
                )}

                {matchLink.isShown && variant === "full" && (
                    <TransferMatchLink
                        onClick={() =>
                            openOverlay("transactionMatch", {
                                transactionId: row.original.id,
                            })
                        }
                        transaction={row.original}
                        matchPreview={matchPreview}
                        documentMatches={matchLink.documentMatches}
                    />
                )}

                <StandardModal
                    show={!!switchingToBusinessCategory}
                    onHide={() => setSwitchingToBusinessCategory(undefined)}
                >
                    <SelectBusinessEntityModal
                        prompt="To assign a business category please select a business entity you'd like to move this transaction to:"
                        onCancel={() =>
                            setSwitchingToBusinessCategory(undefined)
                        }
                        onSelected={onBusinessSelected}
                        selectPlaceholder="Select entity"
                    />
                </StandardModal>
            </ViewTableCell>
        );
    },
    transactionCellAreEqual,
);
CategoryCell.displayName = "CategoryCell";
